import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Timer from "otp-timer";
import notify, { notifyApi } from "../../services/notification.service";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Divider,
} from "@mui/material";

import { AuthContext } from "../../context/AuthContextProvider";
import { loginMobile, OtpVerify, sendWhatsAppOtp } from "../../api/auth";
import {
  ANALYTICS_EVENTS,
  LOGIN_METHOD,
  isFirstLogin,
  logAnalyticsEvent,
} from "../../analytics";




// Served from /public (not bundled through SVGR) — this is a 1.7MB illustration,
// so keeping it out of webpack avoids the Babel "deoptimised" build note.
const loginhome = `${process.env.PUBLIC_URL}/assets/login-home.svg`;
import spandanOneLogo from "../../assets/images/logos/spandan-logo-2.svg";
import OtpInput from "../../components/Reusables/FormControls/FormOtpVerify";
import CountryCodePicker from "../../components/CountryCodePicker";
import LegalLinks from "../../components/LegalLinks";
import {
  getPhoneNumberLimit,
  getPhoneNumberLimitMessage,
  sanitizePhoneNumber,
} from "../../utils/phoneNumber";

const Login = () => {
  const { login, is_login, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [loginType, setLoginType] = useState("phone");
  const [otpMethod, setOtpMethod] = useState(null);
  const phoneLimit = getPhoneNumberLimit(countryCode);
  const [canShowWhatsapp, setCanShowWhatsapp] = useState(false);
  const isPhoneValid = phoneNumber.length === phoneLimit;

  const getDefaultRedirect = () => {
    if (user?.userType === "b2b-doctor") return "/ecg-reports";
    return "/dashboard";
  };

  const redirectTo = location?.state?.path || getDefaultRedirect();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (is_login) {
      navigate(redirectTo, { replace: true });
    }
  }, [is_login, navigate, redirectTo]);
  useEffect(() => {
    if (!otpSent) {
      setCanShowWhatsapp(false);
      return;
    }

    // Show WhatsApp button after 30 seconds
    const timer = setTimeout(() => {
      setCanShowWhatsapp(true);
    }, 30000);

    return () => clearTimeout(timer);
  }, [otpSent]);

  useEffect(() => {
    if (location.state?.phoneNumber) {
      setPhoneNumber(location.state.phoneNumber);
    }
  }, [location.state]);

  /* ---------------- SEND / RESEND OTP ---------------- */
  const sendOtp = async () => {
    if (loginType === "phone" && !isPhoneValid) {
      notify({ message: getPhoneNumberLimitMessage(countryCode), type: "error" });
      return;
    }


    if (loginType === "virtual" && phoneNumber.length < 4) {
      notify({ message: "Enter valid Virtual ID", type: "error" });
      return;
    }
    setSendOtpLoading(true);

    try {
      const payload =
        loginType === "phone"
          ? { countryCode: countryCode, phoneNumber: phoneNumber }
          : { virtualId: phoneNumber };

      const res = await loginMobile(payload);
      notifyApi(res);

      if (!res.success) return;

      setOtp("");
      setOtpSent(true);
      setOtpMethod("sms");
      setCanShowWhatsapp(false);
    } catch {
      notify({ message: "Something went wrong", type: "error" });
    } finally {
      setSendOtpLoading(false);
    }
  };

  /* ---------------- VERIFY OTP ---------------- */
  const verifyOtp = async (enteredOtp) => {
    if (!enteredOtp || enteredOtp.length !== 6) return;

    setVerifyOtpLoading(true);

    try {
      const payload =
        loginType === "phone"
          ? { phoneNumber: countryCode + phoneNumber, otp: enteredOtp, platform: "web" }
          : { virtualId: phoneNumber, platform: "web" };

      const res = await OtpVerify(payload);

      notifyApi(res);

      if (res.success) {
        logAnalyticsEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS, {
          login_method: LOGIN_METHOD.OTP,
          is_first_login: isFirstLogin(res.data),
        });
        login(res.data, res.data.authorization);
      } else {
        logAnalyticsEvent(ANALYTICS_EVENTS.LOGIN_FAILED, {
          login_method: LOGIN_METHOD.OTP,
          failure_reason: res.message,
        });
      }
    } catch {
      logAnalyticsEvent(ANALYTICS_EVENTS.LOGIN_FAILED, {
        login_method: LOGIN_METHOD.OTP,
        failure_reason: "request_threw",
      });
      notify({ message: "OTP verification failed", type: "error" });
    } finally {
      setVerifyOtpLoading(false);
      setOtp("");
    }
  };

  const changeNumber = () => {
    setOtp("");
    setOtpSent(false);
    setVerifyOtpLoading(false);
    setOtpMethod(null);
    setCanShowWhatsapp(false);
  };
  const isPhoneInvalid =
    phoneNumber.length > 0 && phoneNumber.length !== phoneLimit;

  // useEffect(() => {
  //   if (otpSent && otp.length === 6) {
  //     verifyOtp(otp);
  //   }
  // }, [otp]);



  const sendWhatsappOtp = async () => {
    if (!isPhoneValid) {
      notify({ message: getPhoneNumberLimitMessage(countryCode), type: "error" });
      return;
    }
    setOtpMethod('whatsapp')
    setSendOtpLoading(true);

    try {
      const payload = {
        countryCode: countryCode,
        phoneNumber: phoneNumber,
      };

      const res = await sendWhatsAppOtp(payload);

      notifyApi(res);

      if (!res.success) return;

      setOtp("");
      setOtpSent(true);
    } catch {
      notify({ message: "Something went wrong", type: "error" });
    } finally {
      setSendOtpLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Spandan One</title>
      </Helmet>

      <Box sx={{ minHeight: "100vh", display: "flex", backgroundColor: "var(--app-surface)" }}>
        {/* LEFT IMAGE */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: "none", md: "flex" },
            alignItems: "start",
            justifyContent: "start",
            height: "100vh",
            position: "sticky",
            top: 0,
            overflow: "hidden",
          }}
        >
          <img
            src={loginhome}
            alt="login"
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "left top",
            }}
          />
        </Box>

        {/* RIGHT CONTENT */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            px: { xs: 2.5, sm: 3 },
            py: { xs: 5, md: 4 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              my: "auto",
              maxWidth: { xs: "100%", sm: 420, md: "min(80%, 780px)" },
              mx: { xs: "auto", md: 0 },
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            {/* BRAND LOGO (mobile only) */}
            <Box
              component="img"
              src={spandanOneLogo}
              alt="Spandan One"
              sx={{
                height: { xs: 72, sm: 98 },
                mb: "24px",
                display: { xs: "block", md: "none" },
              }}
            />

            {/* TITLE */}
            <Typography
              sx={{
                fontSize: { xs: "22px !important", sm: "26px !important" },
                fontWeight: 700,
                color: "var(--app-text)",
                marginBottom: "29px",
                fontFamily: "Figtree",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Login to your Account
            </Typography>


            {/* CHANGE NUMBER */}
            {otpSent && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mb: "8px",
                }}
              >
                <Typography
                  onClick={changeNumber}
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#5B5BE0",
                    cursor: "pointer",
                  }}
                >
                  Change number?
                </Typography>
              </Box>
            )}

            <Box sx={{ width: "100%", mb: otpSent ? 1 : 2 }}>
              <TextField
                fullWidth
                label={loginType === "phone" ? "Phone Number" : "Virtual ID"}
                InputLabelProps={{ shrink: true }}
                value={phoneNumber}
                disabled={otpSent}
                error={loginType === "phone" && isPhoneInvalid}
                helperText={
                  loginType === "phone" && isPhoneInvalid
                    ? getPhoneNumberLimitMessage(countryCode)
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value;

                  if (loginType === "phone") {
                    setPhoneNumber(sanitizePhoneNumber(v, countryCode));
                  } else {
                    if (/^[a-zA-Z0-9]{0,20}$/.test(v)) setPhoneNumber(v);
                  }
                }}
                onKeyDown={(e) => {
                  const isValid =
                    loginType === "phone"
                      ? isPhoneValid
                      : phoneNumber.length >= 4;

                  if (e.key === "Enter" && !otpSent && isValid) {
                    e.preventDefault();
                    sendOtp();
                  }
                }}
                inputProps={{
                  maxLength: loginType === "phone" ? phoneLimit : 20,
                  inputMode: loginType === "phone" ? "numeric" : "text",
                  autoComplete: "off",
                }}
                InputProps={{
                  startAdornment:
                    loginType === "phone" ? (
                      <InputAdornment position="start" sx={{ height: "auto", mr: 1 }}>
                        <CountryCodePicker
                          value={countryCode}
                          disabled={otpSent}
                          onChange={(nextCountryCode) => {
                            setCountryCode(nextCountryCode);
                            setPhoneNumber((current) =>
                              sanitizePhoneNumber(current, nextCountryCode)
                            );
                          }}
                          size="inline"
                          fullWidth={false}
                        />
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ mx: 1, borderColor: "var(--app-border)" }}
                        />
                      </InputAdornment>
                    ) : null,
                }}
                placeholder={
                  loginType === "phone"
                    ? "Enter your phone number"
                    : "Enter Virtual ID"
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 52,
                    borderRadius: "12px",
                    backgroundColor: "var(--app-surface)",
                  },
                  "& .MuiOutlinedInput-input": {
                    py: "13px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--app-border)",
                  },
                  "& .MuiFormHelperText-root": {
                    mx: 0,
                    mt: 0.75,
                  },
                }}
              />
            </Box>


            {/* OTP INPUT */}
            {otpSent && (
              <>
                <Typography sx={{ fontSize: 14, color: "var(--app-text-muted)", mb: 1 }}>
                  Enter the OTP sent to {countryCode} {phoneNumber}
                </Typography>

                <Box sx={{ width: "100%", mb: 3 }}>
                  <OtpInput
                    length={6}
                    value={otp}
                    onChange={setOtp}
                    disabled={verifyOtpLoading}
                    onComplete={verifyOtp}
                  />
                </Box>
              </>
            )}

            {/* PRIMARY BUTTON */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: !otpSent
                  ? "space-between"
                  : { xs: "center", md: "flex-start" },
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={otpSent ? () => verifyOtp(otp) : sendOtp}
                disabled={
                  sendOtpLoading ||
                  verifyOtpLoading ||
                  (!otpSent && !isPhoneValid)
                }
                sx={{
                  height: 44,
                  minWidth: 150,
                  flexShrink: 0,
                  px: 4,
                  borderRadius: "10px",
                  fontWeight: 600,
                  fontSize: 14,
                  textTransform: "none",
                  backgroundColor: "#5751E9",
                  color: "#ffffff",

                  "&:hover": {
                    backgroundColor: "#5751E9",
                  },

                  "&.Mui-disabled": {
                    backgroundColor: "var(--app-border)",
                    color: "var(--app-text-muted)",
                  },
                }}
              >
                {sendOtpLoading || verifyOtpLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                    {otpSent ? "Verifying OTP" : "Sending OTP"}
                  </>
                ) : otpSent ? (
                  "Verify OTP"
                ) : (
                  "Send OTP"
                )}
              </Button>

              {otpSent && canShowWhatsapp && otpMethod !== "whatsapp" && (
                <Button
                  onClick={sendWhatsappOtp}
                  disabled={sendOtpLoading || verifyOtpLoading}
                  startIcon={<WhatsAppIcon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    color: "#25D366",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Get OTP on WhatsApp
                </Button>
              )}
            </Box>



            {/* RESEND OTP */}
            {otpSent && (
              <Timer
                seconds={30}
                resend={otpMethod === "whatsapp" ? sendWhatsappOtp : sendOtp}
                text={
                  otpMethod === "whatsapp"
                    ? "Resend WhatsApp OTP in"
                    : "Resend OTP in"
                }
                buttonColor="#5B5BE0"
                background="var(--app-surface)"
                afterTimeout={() => setCanShowWhatsapp(true)}
              />
            )}

            {/* LOGIN VIA PASSWORD */}
            <Box sx={{ mt: 1 }}>
              <Link
                to="/loginpassword"
                state={{ phoneNumber }}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#5B5BE0",
                }}
              >
                Login via Password
              </Link>
            </Box>
          </Box>
          <LegalLinks />
        </Box>
      </Box>
    </>
  );
};

export default Login;
